import { memo } from "react";
import { Box, Button, Typography, Theme } from "@mui/material";
import { ChevronLeft, ChevronRight } from "@mui/icons-material";

interface TouchKeyboardProps {
  billetsCount: number;
  onKeypadInput: (digit: string) => void;
  onMoveLeftToRight: () => void;
  onMoveRightToLeft: () => void;
  isLeftToRightDisabled: boolean;
  isRightToLeftDisabled: boolean;
  selectedSourceGroupBilletsCount?: number;
  selectedDestinationGroupBilletsCount?: number;
}

export const TouchKeyboard = memo(
  ({
    billetsCount,
    onKeypadInput,
    onMoveLeftToRight,
    onMoveRightToLeft,
    isLeftToRightDisabled,
    isRightToLeftDisabled,
    selectedSourceGroupBilletsCount,
    selectedDestinationGroupBilletsCount,
  }: TouchKeyboardProps) => {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          justifyContent: "center",
          alignItems: "center",
          height: "100%",
          background: (theme: Theme) =>
            theme.palette.billetYard.backgrounds.gradient,
          border: (theme: Theme) =>
            `1px solid ${theme.palette.billetYard.borders.light}`,
          borderRadius: "12px",
          padding: "16px",
          boxShadow: (theme) => theme.shadows[2],
        }}
      >
        {/* Move Controls */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            marginBottom: "20px",
            width: "90%",
            height: "80px",
            gap: "12px",
          }}
        >
          {/* Left Arrow - Move from Right to Left */}
          <Button
            variant="contained"
            onClick={onMoveRightToLeft}
            disabled={isRightToLeftDisabled}
            sx={{
              width: "32%",
              height: "100%",
              backgroundColor: (theme: Theme) => theme.palette.grey[200],
              borderColor: (theme: Theme) => theme.palette.grey[300],
              borderRadius: "6px",
              fontSize: "33px",
              fontWeight: "bold",
              minWidth: "0",
              border: (theme: Theme) => `1px solid ${theme.palette.grey[300]}`,
              color: (theme: Theme) =>
                isRightToLeftDisabled
                  ? theme.palette.grey[300]
                  : theme.palette.grey[600],
              opacity: isRightToLeftDisabled ? 0.5 : 1,
            }}
          >
            <ChevronLeft sx={{ fontSize: "60px" }} />
          </Button>

          {/* Billets Count Display */}
          <Box
            sx={{
              minWidth: "80px",
              minHeight: "70px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              textAlign: "center",
              fontWeight: "bold",
              fontSize: "24px",
              backgroundColor: (theme: Theme) =>
                theme.palette.billetYard.backgrounds.lighter,
              border: (theme: Theme) => `1px solid ${theme.palette.grey[400]}`,
              borderRadius: "4px",
              color: (theme: Theme) => theme.palette.text.secondary,
              boxShadow: (theme) =>
                `inset 0 1px 2px ${theme.palette.action.selected}`,
              userSelect: "none",
              WebkitUserSelect: "none",
              MozUserSelect: "none",
              msUserSelect: "none",
              flex: "0 0 auto",
            }}
          >
            <Typography
              sx={{
                fontSize: "24px",
                fontWeight: "bold",
                color: (theme: Theme) => theme.palette.primary.main,
                textAlign: "center",
                margin: 0,
                padding: 0,
                lineHeight: 1,
              }}
            >
              {billetsCount === 0 ? "" : billetsCount}
            </Typography>
          </Box>

          {/* Right Arrow - Move from Left to Right */}
          <Button
            variant="contained"
            onClick={onMoveLeftToRight}
            disabled={isLeftToRightDisabled}
            sx={{
              width: "32%",
              height: "100%",
              backgroundColor: (theme: Theme) => theme.palette.grey[200],
              borderColor: (theme: Theme) => theme.palette.grey[300],
              borderRadius: "6px",
              fontSize: "33px",
              fontWeight: "bold",
              minWidth: "0",
              border: (theme: Theme) => `1px solid ${theme.palette.grey[300]}`,
              color: (theme: Theme) =>
                isLeftToRightDisabled
                  ? theme.palette.grey[300]
                  : theme.palette.grey[600],
              opacity: isLeftToRightDisabled ? 0.5 : 1,
            }}
          >
            <ChevronRight sx={{ fontSize: "60px" }} />
          </Button>
        </Box>

        {/* Selected Groups Display */}
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            marginTop: "10px",
            marginBottom: "20px",
            width: "90%",
            gap: "8px",
          }}
        >
          {/* Source Group Billets */}
          <Box
            sx={{
              flex: 1,
              textAlign: "center",
              padding: "8px 12px",
              backgroundColor: (theme: Theme) =>
                theme.palette.billetYard.backgrounds.light,
              border: (theme: Theme) =>
                `1px solid ${theme.palette.billetYard.borders.dark}`,
              borderRadius: "8px",
              minHeight: "40px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: (theme) => theme.shadows[4],
            }}
          >
            <Typography
              variant="caption"
              sx={{
                fontSize: "14px",
                color: (theme: Theme) => theme.palette.text.primary,
                fontWeight: 600,
              }}
            >
              {selectedSourceGroupBilletsCount !== undefined
                ? `Left: ${selectedSourceGroupBilletsCount} billets`
                : "Left: No selection"}
            </Typography>
          </Box>

          {/* Destination Group Billets */}
          <Box
            sx={{
              flex: 1,
              textAlign: "center",
              padding: "8px 12px",
              backgroundColor: (theme: Theme) =>
                theme.palette.billetYard.backgrounds.light,
              border: (theme: Theme) =>
                `1px solid ${theme.palette.billetYard.borders.dark}`,
              borderRadius: "8px",
              minHeight: "40px",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: (theme) => theme.shadows[4],
            }}
          >
            <Typography
              variant="caption"
              sx={{
                fontSize: "14px",
                color: (theme: Theme) => theme.palette.text.primary,
                fontWeight: 600,
              }}
            >
              {selectedDestinationGroupBilletsCount !== undefined
                ? `Right: ${selectedDestinationGroupBilletsCount} billets`
                : "Right: No selection"}
            </Typography>
          </Box>
        </Box>

        {/* Numeric Keyboard */}
        <Box
          sx={{
            marginTop: "30px",
            width: "90%",
            height: "350px",
          }}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              gap: "4px",
              height: "100%",
            }}
          >
            {/* First Row */}
            <Button
              onClick={() => onKeypadInput("7")}
              sx={{
                backgroundColor: "#d0d0d0",
                border: "1px solid #b0b0b0",
                borderRadius: "6px",
                color: "#555555",
                fontSize: "25px",
                fontWeight: "bold",
                minWidth: "0",
                height: "80px",
              }}
            >
              7
            </Button>
            <Button
              onClick={() => onKeypadInput("8")}
              sx={{
                backgroundColor: "#d0d0d0",
                border: "1px solid #b0b0b0",
                borderRadius: "6px",
                color: "#555555",
                fontSize: "25px",
                fontWeight: "bold",
                minWidth: "0",
                height: "80px",
              }}
            >
              8
            </Button>
            <Button
              onClick={() => onKeypadInput("9")}
              sx={{
                backgroundColor: "#d0d0d0",
                border: "1px solid #b0b0b0",
                borderRadius: "6px",
                color: "#555555",
                fontSize: "25px",
                fontWeight: "bold",
                minWidth: "0",
                height: "80px",
              }}
            >
              9
            </Button>

            {/* Second Row */}
            <Button
              onClick={() => onKeypadInput("4")}
              sx={{
                backgroundColor: "#d0d0d0",
                border: "1px solid #b0b0b0",
                borderRadius: "6px",
                color: "#555555",
                fontSize: "25px",
                fontWeight: "bold",
                minWidth: "0",
                height: "80px",
              }}
            >
              4
            </Button>
            <Button
              onClick={() => onKeypadInput("5")}
              sx={{
                backgroundColor: "#d0d0d0",
                border: "1px solid #b0b0b0",
                borderRadius: "6px",
                color: "#555555",
                fontSize: "25px",
                fontWeight: "bold",
                minWidth: "0",
                height: "80px",
              }}
            >
              5
            </Button>
            <Button
              onClick={() => onKeypadInput("6")}
              sx={{
                backgroundColor: "#d0d0d0",
                border: "1px solid #b0b0b0",
                borderRadius: "6px",
                color: "#555555",
                fontSize: "25px",
                fontWeight: "bold",
                minWidth: "0",
                height: "80px",
              }}
            >
              6
            </Button>

            {/* Third Row */}
            <Button
              onClick={() => onKeypadInput("1")}
              sx={{
                backgroundColor: "#d0d0d0",
                border: "1px solid #b0b0b0",
                borderRadius: "6px",
                color: "#555555",
                fontSize: "25px",
                fontWeight: "bold",
                minWidth: "0",
                height: "80px",
              }}
            >
              1
            </Button>
            <Button
              onClick={() => onKeypadInput("2")}
              sx={{
                backgroundColor: "#d0d0d0",
                border: "1px solid #b0b0b0",
                borderRadius: "6px",
                color: "#555555",
                fontSize: "25px",
                fontWeight: "bold",
                minWidth: "0",
                height: "80px",
              }}
            >
              2
            </Button>
            <Button
              onClick={() => onKeypadInput("3")}
              sx={{
                backgroundColor: "#d0d0d0",
                border: "1px solid #b0b0b0",
                borderRadius: "6px",
                color: "#555555",
                fontSize: "25px",
                fontWeight: "bold",
                minWidth: "0",
                height: "80px",
              }}
            >
              3
            </Button>

            {/* Fourth Row */}
            <Button
              onClick={() => onKeypadInput("C")}
              sx={{
                backgroundColor: "#d0d0d0",
                border: "1px solid #b0b0b0",
                borderRadius: "6px",
                color: "#555555",
                fontSize: "20px",
                fontWeight: "bold",
                minWidth: "0",
                height: "80px",
              }}
            >
              C
            </Button>
            <Button
              onClick={() => onKeypadInput("0")}
              sx={{
                backgroundColor: "#d0d0d0",
                border: "1px solid #b0b0b0",
                borderRadius: "6px",
                color: "#555555",
                fontSize: "25px",
                fontWeight: "bold",
                minWidth: "0",
                height: "80px",
              }}
            >
              0
            </Button>
            <Button
              onClick={() => onKeypadInput("←")}
              sx={{
                backgroundColor: "#d0d0d0",
                border: "1px solid #b0b0b0",
                borderRadius: "6px",
                color: "#555555",
                fontSize: "20px",
                fontWeight: "bold",
                minWidth: "0",
                height: "80px",
              }}
            >
              ←
            </Button>
          </Box>
        </Box>
      </Box>
    );
  },
);

TouchKeyboard.displayName = "TouchKeyboard";
